import { useMutation, useQueryClient } from "@tanstack/react-query";
import Tooltip from "@mui/material/Tooltip";
import React, { useEffect, useState, useContext } from "react";
import approvedIcon from "../../../assets/images/approved_icon.jpg";
import infoIcon from "../../../assets/images/info_icon.svg";
import ApproveModal from "../../../components/elements/ApproveModal";
import CorrectionModal from "../../../components/elements/CorrectionModal";
import ExemptionModal from "../../../components/elements/ExemptionModal";
import Modal from "../../../components/elements/Modal";
import SnackbarAlert from "../../../components/elements/SnackbarAlert";
import SuccessModal from "../../../components/elements/SuccessModal";
import useLandingAuthApi from "../../../services/api/useLandingAuthApi";
import { StorageContext } from "../../../services/StorageProvider";
import {
  ClearanceStatus,
  CORRECTION_REASON_ENUM,
  CORRECTION_REASON_VALUES,
  EXEMPTION_REASON_OPTIONS,
  TRIP_CORRECTION_OPTIONS_MULTIPLE_SELECTION,
  TRIP_CORRECTION_OPTIONS_SINGLE_SELECTION,
  TRIP_STATUS,
  TRIP_DETAILS_ACTION_MESSAGES,
  ValidationMessages,
  AIRPORT_TYPE
} from "../../../utils/constants";
import { formatDateTime, getCurrentTimestamp } from "../../../utils/dateUtils";
import { hasAirportAccess } from "../../../utils/Utils";
import "../css/ActionButtons.css";

const ActionButtons = ({
  trackingId,
  tripStatus,
  userData,
  arrivalAirport,
  tripData,
  canTakeAction,
  isRefreshing = false,
}) => {
  const { approveTrip, correctionNeededTrip, exemptTrip } = useLandingAuthApi();
  const { airportData } = useContext(StorageContext);
  const queryClient = useQueryClient();

  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isCorrectionModalOpen, setIsCorrectionModalOpen] = useState(false);
  const [isExemptionModalOpen, setIsExemptionModalOpen] = useState(false);
  const [correctionReason, setCorrectionReason] = useState([]);
  const [commonError, setCommonError] = useState(null);

  // Determine if trip payment is done (feePaid: false && createdByAdsb: true)
  const isMultipleSelectionMode = tripData?.feePaid === false && tripData?.createdByAdsb === true;

  // Determine if trip is a non-payment ADS-B trip (should disable approve button)
  const isNonPaymentAdsBTrip = tripData?.createdByAdsb === true && tripData?.feePaid === false;

  // Select appropriate correction options based on payment status
  const correctionOptions = isMultipleSelectionMode
    ? TRIP_CORRECTION_OPTIONS_MULTIPLE_SELECTION
    : TRIP_CORRECTION_OPTIONS_SINGLE_SELECTION;

  const [localTripStatus, setLocalTripStatus] = useState(tripStatus);
  const tripId = tripData?.id;
  const departure = formatDateTime(tripData?.departureTime);
  const isExempted = tripData?.isExempted;

  useEffect(() => {
    setLocalTripStatus(tripStatus);
  }, [tripStatus, tripId]);

  const updateTripStatusCache = (status) => {
    if (!status || !tripId) {
      return;
    }

    setLocalTripStatus(status);
    queryClient.setQueryData(["tripDetails", tripId], (cachedTripDetails) => {
      if (!cachedTripDetails?.data) {
        return cachedTripDetails;
      }

      return {
        ...cachedTripDetails,
        data: {
          ...cachedTripDetails.data,
          status,
        },
      };
    });
  };

  const invalidateTripQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["tripDetails", tripId] });
    queryClient.invalidateQueries({ queryKey: ["historicTripDetails", tripId] });
    queryClient.invalidateQueries({ queryKey: ["trips"] });
    queryClient.invalidateQueries({ queryKey: ["filters-trips"] });
    queryClient.invalidateQueries({ queryKey: ["historic-trips"] });
  };

  const [successModalData, setSuccessModalData] = useState({
    isOpen: false,
    title: "",
    message: "",
    image: null,
    buttonLabel: "",
  });

  const closeSuccessModal = () => {
    setSuccessModalData({
      isOpen: false,
      title: "",
      message: "",
      image: null,
      buttonLabel: "",
    });
  };

  const { mutate: approveTripMutate, isPending: isApprovePending } =
    useMutation({
      mutationFn: ({ tripId, data }) => approveTrip(tripId, data),
      onSuccess: (response) => {
        updateTripStatusCache(response?.data?.status);
        invalidateTripQueries();
        setSuccessModalData({
          isOpen: true,
          message: "You have successfully approved the trip and payment",
          buttonLabel: "Okay",
        });
        setIsApproveModalOpen(false);
      },
      onError: (error) => {
        setCommonError(error.message);

        setIsApproveModalOpen(false);
      },
    });

  const getStatus = (status) => {
    if (status === ClearanceStatus.NOT_REQUIRED) return null;
    if (status === ClearanceStatus.PAID) return true;
    if (status === ClearanceStatus.CLEARANCE_REJECTED) return true;
    if (status === ClearanceStatus.CANCELLED) return true;

    return false;
  };

  const statusData = {
    escortArrivalStatus: getStatus(tripData?.escortArrivalStatus),
    escortDepartureStatus: getStatus(tripData?.escortDepartureStatus),
    overtimeArrivalStatus: getStatus(tripData?.overtimeArrivalStatus),
    overtimeDepartureStatus: getStatus(tripData?.overtimeDepartureStatus),
  };
  const validateApproval = (event) => {
    event.preventDefault();
    setCommonError(null);

    let errorMessage = null;
    let valid = true;

    if (
      tripData?.escortArrivalStatus === undefined ||
      tripData?.escortDepartureStatus === undefined ||
      tripData?.overtimeArrivalStatus === undefined ||
      tripData?.overtimeDepartureStatus === undefined
    ) {
      valid = true;
    } else if (statusData?.escortArrivalStatus === false) {
      errorMessage = ValidationMessages.ESCORT_ARRIVAL_APPROVAL_REQUIRED;
      valid = false;
    } else if (statusData?.escortDepartureStatus === false) {
      errorMessage = ValidationMessages.ESCORT_DEPARTURE_APPROVAL_REQUIRED;
      valid = false;
    } else if (
      statusData?.overtimeArrivalStatus === false ||
      statusData?.overtimeDepartureStatus === false
    ) {
      errorMessage = ValidationMessages.AFTER_HOURS_APPROVAL_REQUIRED;
      valid = false;
    }

    setTimeout(() => {
      setCommonError(errorMessage);
    }, 0);
    return valid;
  };
  const handleApproveTrip = () => {
    approveTripMutate({
      tripId,
      data: userData?.email,
    });
  };

  const { mutate: exemptTripMutate, isPending: isExemptPending } =
    useMutation({
      mutationFn: ({ tripId, exemptionReason }) =>
        exemptTrip(tripId, exemptionReason),
      onSuccess: () => {
        setSuccessModalData({
          isOpen: true,
          title: "Trip Exempted",
          message: "The trip has been successfully exempted.",
          buttonLabel: "Okay",
        });
        setIsExemptionModalOpen(false);
        invalidateTripQueries();
      },
      onError: (error) => {
        setCommonError(error.message);
        setIsExemptionModalOpen(false);
      },
    });

  const { mutate: correctionNeededTripMutate, isPending: isCorrectionPending } =
    useMutation({
      mutationFn: ({ tripId, data }) => correctionNeededTrip(tripId, data),
      onSuccess: (response) => {
        updateTripStatusCache(
          response?.data?.status || TRIP_STATUS.CORRECTION_REQUESTED
        );
        invalidateTripQueries();
        setSuccessModalData({
          isOpen: true,
          title: "Correction Needed",
          message: "Correction is requested for this Trip",
        });
        setIsCorrectionModalOpen(false);
      },
      onError: (error) => {
        setCommonError(error.message);
        setIsCorrectionModalOpen(false);
      },
    });

  const mapCorrectionReasonsToEnum = (selectedReasons) => {
    const hasArrivalTerminal = selectedReasons.includes(CORRECTION_REASON_VALUES.ARRIVAL_TERMINAL_REQUIRED);
    const hasDepartureTerminal = selectedReasons.includes(CORRECTION_REASON_VALUES.DEPARTURE_TERMINAL_REQUIRED);
    const hasPassenger = selectedReasons.includes(CORRECTION_REASON_VALUES.PASSENGER_REQUIRED);

    // Check for combination 4: All three selected
    if (hasArrivalTerminal && hasDepartureTerminal && hasPassenger) {
      return CORRECTION_REASON_ENUM.DEPARTURE_ARRIVAL_TERMINALS_AND_PASSENGER_REQUIRED;
    }

    // Check for combination 1: Arrival + Departure terminals
    if (hasArrivalTerminal && hasDepartureTerminal) {
      return CORRECTION_REASON_ENUM.DEPARTURE_AND_ARRIVAL_TERMINALS_REQUIRED;
    }

    // Check for combination 2: Departure terminal + Passenger
    if (hasDepartureTerminal && hasPassenger) {
      return CORRECTION_REASON_ENUM.DEPARTURE_TERMINAL_AND_PASSENGER_REQUIRED;
    }

    // Check for combination 3: Arrival terminal + Passenger
    if (hasArrivalTerminal && hasPassenger) {
      return CORRECTION_REASON_ENUM.ARRIVAL_TERMINAL_AND_PASSENGER_REQUIRED;
    }

    // If no combination matches, return the original reasons joined (for backward compatibility)
    // or handle single selections
    return selectedReasons.join(", ");
  };

  const handleCorrectNeeded = (event) => {
    event.preventDefault();

    // For single selection, correctionReason is a string
    // For multiple selection, correctionReason is an array
    if (isMultipleSelectionMode) {
      if (!Array.isArray(correctionReason) || correctionReason.length === 0) {
        setCommonError("Please select at least one reason for correction");
        return;
      }
      // Map selected reasons to enum value for multiple selection
      const correctionReasonEnum = mapCorrectionReasonsToEnum(correctionReason);
      correctionNeededTripMutate({
        tripId,
        data: {
          correctionReason: correctionReasonEnum,
        },
      });
    } else {
      // For single selection, correctionReason is a string
      if (!correctionReason || correctionReason.trim() === "") {
        setCommonError("Please select a reason for correction");
        return;
      }
      correctionNeededTripMutate({
        tripId,
        data: {
          correctionReason: correctionReason,
        },
      });
    }
  };

  const handleApproveClick = (event) => {
    if (!validateApproval(event)) {
      return;
    }
    setIsApproveModalOpen(true);
  };

  const handleCorrectionClick = () => {
    setIsCorrectionModalOpen(true);
    // Initialize based on selection mode
    setCorrectionReason(isMultipleSelectionMode ? [] : "");
    setCommonError(null);
  };

  const handleExemptionClick = () => {
    setIsExemptionModalOpen(true);
    setCommonError(null);
  };

  const handleExemptTrip = (exemptionReason) => {
    exemptTripMutate({
      tripId,
      exemptionReason,
    });
  };

  const handleExemptionCancel = () => {
    setIsExemptionModalOpen(false);
  };

  const handleCorrectionReasonChange = (event) => {
    if (isMultipleSelectionMode) {
      // Multiple selection: handle checkboxes
      const { value, checked } = event.target;
      if (checked) {
        // Add the value to the array if checked
        setCorrectionReason(prev => {
          const prevArray = Array.isArray(prev) ? prev : [];
          return [...prevArray, value];
        });
      } else {
        // Remove the value from the array if unchecked
        setCorrectionReason(prev => {
          const prevArray = Array.isArray(prev) ? prev : [];
          return prevArray.filter(reason => reason !== value);
        });
      }
    } else {
      // Single selection: handle radio buttons
      const { value } = event.target;
      setCorrectionReason(value);
    }
  };

  const isApproved = localTripStatus === TRIP_STATUS.COMPLETED;
  const isReadyToPay = localTripStatus === TRIP_STATUS.READY_TO_PAY;
  const isCorrectionNeeded =
    localTripStatus === TRIP_STATUS.CORRECTION_REQUESTED;

  // Check if user has access to the departure airport
  const hasDepartureAirportAccess = hasAirportAccess(
    userData,
    airportData,
    tripData?.departureAirport?.id
  );

  // Check if user has access to the arrival airport
  const hasArrivalAirportAccess = hasAirportAccess(
    userData,
    airportData,
    tripData?.arrivalAirport?.id
  );

  // Determine if trip is departing from an international airport
  const isInternationalDeparture = tripData?.departureAirport?.airportType === AIRPORT_TYPE.INTL;

  const departureAirportType = tripData?.departureAirport?.airportType;
  const isDomesticDeparture = departureAirportType === AIRPORT_TYPE.DOM;
  // If departure is DOM and has terminal, approval should be based on arrival airport access
  const isDomesticDepartureWithTerminal =
    isDomesticDeparture &&
    tripData?.departureAirportTerminal != null;

  const isNonPaymentAdsBTripBlocked = isNonPaymentAdsBTrip && !isDomesticDepartureWithTerminal;

  const isDomesticArrival = tripData?.arrivalAirport?.airportType === AIRPORT_TYPE.DOM;
  const isDomesticDepartureMainAndArrivalMain =
    isDomesticDeparture &&
    isDomesticArrival &&
    tripData?.departureAirportTerminal == null &&
    tripData?.arrivalAirportTerminal == null;

  // Determine if user can approve based on airport type and access
  const canApproveTrip = isDomesticDepartureMainAndArrivalMain
    ? hasDepartureAirportAccess || hasArrivalAirportAccess // DOM main -> DOM main: either airport access can approve
    : isInternationalDeparture || isDomesticDepartureWithTerminal
      ? hasArrivalAirportAccess // For international departures OR DOM w/ terminal, need arrival airport access
      : hasDepartureAirportAccess; // Otherwise, need departure airport access

  const isApproveDisabled =
    isRefreshing ||
    isApproved ||
    isReadyToPay ||
    !canTakeAction ||
    isCorrectionNeeded ||
    !canApproveTrip ||
    isNonPaymentAdsBTripBlocked ||
    isExempted;
  const hasApproveDisabledClass =
    isRefreshing ||
    isReadyToPay ||
    !canTakeAction ||
    isCorrectionNeeded ||
    !canApproveTrip ||
    isNonPaymentAdsBTripBlocked ||
    isExempted;
  const isCorrectionDisabled =
    isRefreshing ||
    isCorrectionNeeded ||
    isApproved ||
    !canTakeAction ||
    isExempted;
  const hasCorrectionDisabledClass =
    isRefreshing || isApproved || !canTakeAction || isExempted;

  //const canTakeAction = isDepartureReached();

  return (
    <div>
      {commonError && <SnackbarAlert type="error">{commonError}</SnackbarAlert>}
      {!isReadyToPay && !canApproveTrip && canTakeAction && (
        <div className="action-buttons-message">
          <span className="info-icon">ℹ️</span>
          {isInternationalDeparture || isDomesticDepartureWithTerminal
            ? TRIP_DETAILS_ACTION_MESSAGES.NO_ARRIVAL_AIRPORT_ACCESS
            : TRIP_DETAILS_ACTION_MESSAGES.NO_DEPARTURE_AIRPORT_ACCESS
          }
        </div>
      )}
      {!isReadyToPay && isNonPaymentAdsBTripBlocked && canTakeAction && (
        <div className="action-buttons-message">
          <span className="info-icon">ℹ️</span>
          {TRIP_DETAILS_ACTION_MESSAGES.NON_PAYMENT_ADSB_TRIP}
        </div>
      )}
      {isReadyToPay && canTakeAction && (
        <div className="action-buttons-message">
          <span className="info-icon">ℹ️</span>
          {TRIP_DETAILS_ACTION_MESSAGES.NON_PAYMENT_TRIP}
        </div>
      )}
      <div className="trip-details-action-buttons">
        <button
          className={`trip-details-btn trip-details-approve-btn ${hasApproveDisabledClass
            ? "disabled"
            : isApproved
              ? "approved not-clickable"
              : "trip-details-approve-btn"
            }`}
          onClick={handleApproveClick}
          disabled={isApproveDisabled}
        >
          {isApproved ? "Approved" : "Approve"}
        </button>
        {tripData?.createdByAdsb && (
          isExempted ? (
            <div className="trip-details-btn trip-details-exemption-btn disabled exemption-btn-with-icon">
              <span>Exemption</span>
              <Tooltip title={EXEMPTION_REASON_OPTIONS.find((opt) => opt.value === tripData?.tripExemptionReason)?.label ?? tripData?.tripExemptionReason ?? ""}>
                <img src={infoIcon} alt="Exemption reason" className="exemption-info-icon" />
              </Tooltip>
            </div>
          ) : (
            <button
              className="trip-details-btn trip-details-exemption-btn"
              onClick={handleExemptionClick}
            >
              Exemption
            </button>
          )
        )}
        <button
          className={`trip-details-btn trip-details-correction-btn ${hasCorrectionDisabledClass
            ? "disabled"
            : "trip-details-correction-btn"
            }`}
          onClick={handleCorrectionClick}
          disabled={isCorrectionDisabled}
        >
          {isCorrectionNeeded ? "Correction Requested" : "Correction Needed"}
        </button>
      </div>
      <Modal
        isOpen={isCorrectionModalOpen}
        onClose={() => setIsCorrectionModalOpen(false)}
      >
        <CorrectionModal
          onCorrectNeeded={handleCorrectNeeded}
          reasonForCorrection={handleCorrectionReasonChange}
          value={correctionReason}
          isLoading={isCorrectionPending}
          correctionOptions={correctionOptions}
          allowMultipleSelection={isMultipleSelectionMode}
        />
      </Modal>
      <Modal
        isOpen={isApproveModalOpen}
        onClose={() => setIsApproveModalOpen(false)}
      >
        <ApproveModal
          email={userData?.email}
          tripId={trackingId}
          airport={arrivalAirport}
          timestamp={getCurrentTimestamp()}
          onApprove={handleApproveTrip}
          isLoading={isApprovePending}
          message={"Do you want to approve the Trip?"}
        />
      </Modal>
      <Modal
        isOpen={isExemptionModalOpen}
        onClose={() => setIsExemptionModalOpen(false)}
        disableClose={isExemptPending}
      >
        <ExemptionModal
          onExempt={handleExemptTrip}
          onCancel={handleExemptionCancel}
          isLoading={isExemptPending}
        />
      </Modal>
      <SuccessModal
        isOpen={successModalData.isOpen}
        onClose={closeSuccessModal}
        title={successModalData.title}
        buttonLabel={successModalData.buttonLabel}
        image={approvedIcon}
        message={successModalData.message}
      />
    </div>
  );
};

export default ActionButtons;
